import { useHandler } from "@chatbotgang/etude/react/useHandler";
import AdminPanelSettingsOutlined from "@mui/icons-material/AdminPanelSettingsOutlined";
import ChevronRightOutlined from "@mui/icons-material/ChevronRightOutlined";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Popover from "@mui/material/Popover";
import type { SxProps } from "@mui/material/styles";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { stringToColor } from "@polifonia/theme/colors";
import { useGateValue } from "@statsig/react-bindings";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { RouterLink } from "@/components/RouterLink";
import { Typography } from "@/components/Typography";
import { interlude } from "@/interlude";
import { BackToProductLink } from "@/layout/MainContent/BackToProductLink";

const styles = {
  rowTrigger: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    width: "100%",
    padding: "8px",
    borderRadius: "6px",
    textAlign: "left",
    "&:hover": {
      backgroundColor: theme.colors.neutral010,
    },
  },
  compactTrigger: {
    borderRadius: "8px",
  },
  triggerText: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: 0,
  },
  ellipsis: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  chevron: {
    fontSize: "1rem",
    color: theme.colors.staticFgNote,
  },
  panel: {
    display: "flex",
    flexDirection: "column",
    width: "340px",
  },
  panelHeader: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "20px 20px 16px",
  },
  details: {
    display: "grid",
    gridTemplateColumns: "112px minmax(0, 1fr)",
    rowGap: "10px",
    columnGap: "12px",
    margin: "0px",
    padding: "12px 20px",
    borderTop: `1px solid ${theme.colors.staticFgLine}`,
  },
  detailValue: {
    margin: "0px",
    overflowWrap: "anywhere",
  },
  actions: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    padding: "12px 20px 16px",
    borderTop: `1px solid ${theme.colors.staticFgLine}`,
  },
  actionLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
  },
  actionIcon: {
    fontSize: "1rem",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

const avatarColors = [
  theme.colors.yellow050,
  theme.colors.green050,
  theme.colors.purple050,
  theme.colors.red050,
];

/** Product codes in the token, shown as their product names. */
const productNames: Record<string, string> = {
  maac: "MAAC",
  caac: "CAAC",
  daac: "DAAC",
};

const OrgAvatar: React.FC<{ name: string; size: number }> = function OrgAvatar({
  name,
  size,
}) {
  return (
    <Avatar
      sx={{
        width: `${size}px`,
        height: `${size}px`,
        fontSize: size > 32 ? "1.125rem" : "1rem",
        fontWeight: 600,
        borderRadius: "8px",
        bgcolor: stringToColor(name, avatarColors),
      }}
    >
      {name.substring(0, 1)}
    </Avatar>
  );
};

/**
 * The MAAC roles of the signed-in user, looked up by email in the org's MAAC
 * member list. `null` until found, so nothing is shown for a user MAAC does
 * not list (for example one who came from DAAC).
 */
const useMyMaacRoles = (email: string | undefined, enabled: boolean) => {
  const usersQuery = interlude.user.useUsers(
    { queries: { searchFilter: email, pageSize: 10 } },
    { enabled: enabled && Boolean(email) },
  );
  return useMemo(() => {
    const me = usersQuery.data?.find((user) => user.email === email);
    if (!me || me.roles.length === 0) return null;
    return me.roles.map((role) => role.name).join(", ");
  }, [email, usersQuery.data]);
};

interface AccountMenuProps {
  /** Shows only the avatar, for the collapsed side menu. */
  compact: boolean;
}

/**
 * Who is signed in, at the bottom of the side menu. The trigger shows the
 * organization avatar and the signed-in email; the panel adds the MAAC role,
 * the product the user came from, and links to product access and back to
 * that product. Every row appears only when its data is known.
 */
export const AccountMenu: React.FC<AccountMenuProps> = function AccountMenu({
  compact,
}) {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = Boolean(anchor);
  const meQuery = interlude.auth.useGetMe();
  const myPermissionsQuery = interlude.user.useMyPermissions();
  const email = myPermissionsQuery.data?.email;
  const roles = useMyMaacRoles(email, open);
  const canManageAccess = useGateValue("user_management");

  const handleOpen = useHandler<React.MouseEventHandler<HTMLButtonElement>>(
    (event) => setAnchor(event.currentTarget),
  );
  const handleClose = useHandler(() => setAnchor(null));

  const organizationName = meQuery.data?.organization.name;
  if (!organizationName) return null;
  const source = meQuery.data?.source;
  const sourceName = source ? (productNames[source] ?? source) : null;

  return (
    <>
      <ButtonBase
        sx={compact ? styles.compactTrigger : styles.rowTrigger}
        onClick={handleOpen}
        aria-label={t("account.menu.open", "Account")}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <OrgAvatar name={organizationName} size={32} />
        {compact ? null : (
          <>
            <Box sx={styles.triggerText}>
              <Box sx={styles.ellipsis}>
                <Typography variant="body" fontWeight={500}>
                  {email ?? organizationName}
                </Typography>
              </Box>
              {email ? (
                <Box sx={styles.ellipsis}>
                  <Typography variant="note" color={theme.colors.staticFgNote}>
                    {organizationName}
                  </Typography>
                </Box>
              ) : null}
            </Box>
            <ChevronRightOutlined sx={styles.chevron} />
          </>
        )}
      </ButtonBase>
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box
          component="section"
          aria-label={t("account.menu.title", "Your account")}
          sx={styles.panel}
        >
          <Box sx={styles.panelHeader}>
            <OrgAvatar name={organizationName} size={40} />
            <Box sx={styles.triggerText}>
              <Box sx={styles.ellipsis}>
                <Typography variant="body" fontWeight={600}>
                  {organizationName}
                </Typography>
              </Box>
              {email ? (
                <Box sx={styles.ellipsis}>
                  <Typography variant="note" color={theme.colors.staticFgNote}>
                    {email}
                  </Typography>
                </Box>
              ) : null}
            </Box>
          </Box>
          {roles || sourceName ? (
            <Box component="dl" sx={styles.details}>
              {roles ? (
                <>
                  <Box component="dt">
                    <Typography
                      variant="note"
                      color={theme.colors.staticFgNote}
                    >
                      {t("account.menu.maacRole", "Role in MAAC")}
                    </Typography>
                  </Box>
                  <Box component="dd" sx={styles.detailValue}>
                    <Typography variant="body">{roles}</Typography>
                  </Box>
                </>
              ) : null}
              {sourceName ? (
                <>
                  <Box component="dt">
                    <Typography
                      variant="note"
                      color={theme.colors.staticFgNote}
                    >
                      {t("account.menu.signedInFrom", "Signed in from")}
                    </Typography>
                  </Box>
                  <Box component="dd" sx={styles.detailValue}>
                    <Typography variant="body">{sourceName}</Typography>
                  </Box>
                </>
              ) : null}
            </Box>
          ) : null}
          <Box sx={styles.actions}>
            {canManageAccess ? (
              <RouterLink
                to="/user-management"
                onClick={handleClose}
                sx={styles.actionLink}
              >
                <AdminPanelSettingsOutlined sx={styles.actionIcon} />
                {t("account.menu.productAccess", "Manage product access")}
              </RouterLink>
            ) : null}
            <BackToProductLink source={source} />
          </Box>
        </Box>
      </Popover>
    </>
  );
};
